/**
 * TrackOrder.js - Guest Order Tracking Page
 * Phase E2: Allow guests to track orders without account
 *
 * Features:
 * - Email + order number lookup
 * - Order status display
 * - Account creation prompt for guests
 * - Security: Rate limiting, input validation
 */

import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
import {
  formatCurrency,
  formatDate as formatDateTz,
} from "../../utils/displayHelpers";
import "./TrackOrder.css";

const TrackOrder = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [orderNumber, setOrderNumber] = useState(
    searchParams.get("order") || "",
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);
  const [showAccountPrompt, setShowAccountPrompt] = useState(false);

  // Auto-fetch if params provided
  useEffect(() => {
    if (email && orderNumber) {
      handleTrackOrder();
    }
  }, []);

  const handleTrackOrder = async (e) => {
    if (e) e.preventDefault();

    setLoading(true);
    setError("");
    setOrder(null);

    // Validation
    if (!email || !orderNumber) {
      setError("Please enter both email and order number");
      setLoading(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/orders/track?email=${encodeURIComponent(email)}&orderNumber=${encodeURIComponent(orderNumber)}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Unable to find order");
      }

      setOrder(data.order);

      // Show account creation prompt if guest order and no account
      if (data.order.isGuestOrder && data.order.accountCreationToken) {
        setShowAccountPrompt(true);
      }
    } catch (err) {
      setError(
        err.message ||
          "Unable to find order. Please check your email and order number.",
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      processing: "#f59e0b",
      confirmed: "#10b981",
      completed: "#3b82f6",
      cancelled: "#ef4444",
    };
    return colors[status] || "#6b7280";
  };

  const formatOrderStatusLabel = (status) => {
    const normalized = String(status || "").trim();
    if (!normalized) return "UNKNOWN";
    return normalized.replace(/_/g, " ").toUpperCase();
  };

  const getShopDisplayName = (shop) => {
    return (
      shop?.shopDisplayName ||
      shop?.shopName ||
      shop?.shopId?.name ||
      "Shop"
    );
  };

  const getProductReturnUrl = (orderRow) => {
    const emailValue = String(orderRow?.customerEmail || "").trim();
    const orderValue = String(orderRow?.orderNumber || "").trim();
    return `/refund-service?tab=product&email=${encodeURIComponent(emailValue)}&order=${encodeURIComponent(orderValue)}`;
  };

  return (
    <>
      <CustomerHeader />
      <div className="track-order-page">
        <div className="track-order-container">
          <div className="track-order-header">
            <h1>📦 Track Your Order</h1>
            <p>Enter your email and order number to view your order status</p>
          </div>

          {/* Tracking Form */}
          <form onSubmit={handleTrackOrder} className="tracking-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="orderNumber">Order Number</label>
              <input
                type="text"
                id="orderNumber"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value.toUpperCase())}
                placeholder="OGE-2026-1234"
                required
              />
              <small>Find this in your confirmation email</small>
            </div>

            {error && (
              <div className="error-message">
                <span>⚠️</span>
                {error}
              </div>
            )}

            <button type="submit" className="track-btn" disabled={loading}>
              {loading ? "Searching..." : "Track Order"}
            </button>
          </form>

          {/* Order Details */}
          {order && (
            <div className="order-details-section">
              <div className="order-header">
                <div className="order-number-badge">
                  <span className="label">Order Number</span>
                  <span className="number">{order.orderNumber}</span>
                </div>
                <div
                  className="order-status"
                  style={{
                    borderColor: getStatusColor(order.status),
                    backgroundColor: getStatusColor(order.status),
                    color: "#ffffff",
                  }}
                >
                  <span style={{ color: "#ffffff", fontWeight: "600" }}>
                    {formatOrderStatusLabel(order.status)}
                  </span>
                </div>
              </div>

              <div className="order-info">
                <div className="info-row">
                  <span className="label">Order Date:</span>
                  <span className="value">{formatDate(order.createdAt)}</span>
                </div>
                <div className="info-row">
                  <span className="label">Customer:</span>
                  <span className="value">
                    {order.customerName.firstName} {order.customerName.lastName}
                  </span>
                </div>
                <div className="info-row">
                  <span className="label">Email:</span>
                  <span className="value">{order.customerEmail}</span>
                </div>
              </div>

              {/* Shop Sections */}
              {order.shops.map((shop, idx) => (
                <div key={idx} className="shop-section">
                  <h3 className="shop-name">🏪 {getShopDisplayName(shop)}</h3>
                  <p style={{ marginBottom: "12px", fontSize: "14px" }}>
                    For returns/refunds, <a href={getProductReturnUrl(order)}>click here</a>.
                  </p>

                  {/* Products */}
                  {shop.items && shop.items.length > 0 && (
                    <div className="items-section">
                      <h4>Products</h4>
                      {shop.items.map((item, itemIdx) => (
                        <div key={itemIdx} className="order-item">
                          <div className="item-info">
                            <span className="item-name">{item.name}</span>
                            <span className="item-quantity">
                              Qty: {item.quantity}
                            </span>
                          </div>
                          <span className="item-price">
                            {formatCurrency(
                              item.priceAtPurchase * item.quantity,
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Bookings */}
                  {shop.bookings && shop.bookings.length > 0 && (
                    <div className="items-section">
                      <h4>Service Appointments</h4>
                      {shop.bookings.map((booking, bookingIdx) => (
                        <div key={bookingIdx} className="booking-item">
                          <div className="booking-info">
                            <span className="service-name">
                              {booking.serviceName}
                            </span>
                            {booking.professionalName && (
                              <span className="professional">
                                with {booking.professionalName}
                              </span>
                            )}
                            {booking.appointmentDate && booking.startTime && (
                              <span className="appointment-time">
                                📅{" "}
                                {new Intl.DateTimeFormat("en-US", {
                                  weekday: "short",
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                  hour: "numeric",
                                  minute: "2-digit",
                                  timeZone:
                                    booking.shopTimezone || "America/Chicago",
                                }).format(
                                  new Date(
                                    `${booking.appointmentDate}T${booking.startTime}:00`,
                                  ),
                                )}
                              </span>
                            )}
                          </div>
                          <span className="booking-price">
                            {formatCurrency(
                              booking.servicePrice || booking.amount || 0,
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Products from Bookings */}
                  {shop.bookings &&
                    shop.bookings.some(
                      (b) => b.products && b.products.length > 0,
                    ) && (
                      <div className="items-section">
                        <h4>Products for Pickup</h4>
                        {shop.bookings.map(
                          (booking, bookingIdx) =>
                            booking.products &&
                            booking.products.map((product, productIdx) => (
                              <div
                                key={`${bookingIdx}-${productIdx}`}
                                className="order-item"
                              >
                                <div className="item-info">
                                  <span className="item-name">
                                    {product.productName}
                                  </span>
                                  <span className="item-quantity">
                                    Qty: {product.quantity}
                                  </span>
                                  <span className="pickup-badge">
                                    Pickup at appointment
                                  </span>
                                </div>
                                <span className="item-price">
                                  {formatCurrency(
                                    product.priceAtBooking * product.quantity,
                                  )}
                                </span>
                              </div>
                            )),
                        )}
                      </div>
                    )}

                  <div className="shop-contact">
                    <p>
                      📞 Questions? Use the return link above or contact the shop directly.
                    </p>
                  </div>
                </div>
              ))}

              {/* Order Total */}
              <div className="order-total-section">
                {order.subtotal > 0 && (
                  <>
                    <div className="total-row">
                      <span className="label">Subtotal:</span>
                      <span className="value">
                        {formatCurrency(order.subtotal)}
                      </span>
                    </div>
                    {order.taxAmount > 0 && (
                      <div className="total-row">
                        <span className="label">Tax:</span>
                        <span className="value">
                          {formatCurrency(order.taxAmount)}
                        </span>
                      </div>
                    )}
                  </>
                )}
                <div className="total-row grand-total">
                  <span className="label">Total Paid:</span>
                  <span className="value">
                    {formatCurrency(order.totalAmount)}
                  </span>
                </div>
              </div>

              {/* Account Creation Prompt */}
              {showAccountPrompt && (
                <div className="account-prompt">
                  <h3>🎉 Create Your Account</h3>
                  <p>
                    Track all your orders, rebook with favorite professionals,
                    and manage appointments easily!
                  </p>
                  <div className="benefits-list">
                    <span>✅ Order history in one place</span>
                    <span>✅ Easy rebooking</span>
                    <span>✅ Exclusive offers</span>
                    <span>✅ Save favorites</span>
                  </div>
                  <button
                    className="create-account-btn"
                    onClick={() => {
                      // Navigate to account creation with token
                      window.location.href = `/create-account?token=${order.accountCreationToken}`;
                    }}
                  >
                    Create Free Account
                  </button>
                  <button
                    className="dismiss-btn"
                    onClick={() => setShowAccountPrompt(false)}
                  >
                    Maybe Later
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Help Section */}
          <div className="help-section">
            <h3>Need Help?</h3>
            <p>
              Can't find your order? Make sure you're using the same email
              address you used at checkout.
            </p>
            <p>
              Order numbers start with "OGE-" and can be found in your
              confirmation email.
            </p>
          </div>
        </div>
      </div>
      <CustomerFooter />
    </>
  );
};

export default TrackOrder;
